// Bibliothèque statistique pure (number[] / paires x,y) — socle des analyses.

/** Somme des éléments (0 pour une liste vide). */
export function sum(values: readonly number[]): number {
  return values.reduce((acc, v) => acc + v, 0);
}

/** Moyenne arithmétique (NaN si vide). */
export function mean(values: readonly number[]): number {
  if (values.length === 0) return NaN;
  return sum(values) / values.length;
}

/** Médiane (moyenne des 2 centraux si effectif pair ; NaN si vide). */
export function median(values: readonly number[]): number {
  if (values.length === 0) return NaN;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1]! + sorted[mid]!) / 2;
  }
  return sorted[mid]!;
}

/** Minimum (NaN si vide). */
export function min(values: readonly number[]): number {
  return values.length ? Math.min(...values) : NaN;
}

/** Maximum (NaN si vide). */
export function max(values: readonly number[]): number {
  return values.length ? Math.max(...values) : NaN;
}

/** Étendue (max - min). */
export function range(values: readonly number[]): number {
  return max(values) - min(values);
}

/** Variance : diviseur N (population) ou N-1 (échantillon, non biaisée). NaN si n<2 en mode sample. */
export function variance(values: readonly number[], sample = false): number {
  const n = values.length;
  if (n === 0) return NaN;
  if (sample && n < 2) return NaN;
  const m = mean(values);
  const ss = values.reduce((acc, v) => acc + (v - m) ** 2, 0);
  return ss / (sample ? n - 1 : n);
}

/** Écart-type (racine de la variance). */
export function stddev(values: readonly number[], sample = false): number {
  return Math.sqrt(variance(values, sample));
}

/** Coefficient de variation (écart-type / moyenne) : dispersion relative, comparable entre échelles. */
export function coefficientOfVariation(values: readonly number[]): number {
  const m = mean(values);
  if (m === 0) return NaN;
  return stddev(values, true) / Math.abs(m);
}

/** Percentile p (dans [0,100]) par interpolation linéaire (méthode R-7, défaut NumPy). */
export function percentile(values: readonly number[], p: number): number {
  if (values.length === 0) return NaN;
  if (p <= 0) return min(values);
  if (p >= 100) return max(values);
  const sorted = [...values].sort((a, b) => a - b);
  const rank = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  if (lo === hi) return sorted[lo]!;
  const frac = rank - lo;
  return sorted[lo]! + frac * (sorted[hi]! - sorted[lo]!);
}

/** Écart interquartile (P75 - P25), mesure de dispersion robuste. */
export function iqr(values: readonly number[]): number {
  return percentile(values, 75) - percentile(values, 25);
}

// Moyenne mobile simple. Même longueur que l'entrée ; les window-1 premiers points ont une fenêtre partielle.
export function movingAverage(values: readonly number[], window: number): number[] {
  if (window < 1) throw new Error('window doit être >= 1');
  return values.map((_, i) => {
    const start = Math.max(0, i - window + 1);
    return mean(values.slice(start, i + 1));
  });
}

// Moyenne mobile exponentielle. alpha dans ]0,1] : plus grand = plus réactif.
export function exponentialMovingAverage(values: readonly number[], alpha: number): number[] {
  if (alpha <= 0 || alpha > 1) throw new Error('alpha doit être dans ]0, 1]');
  const out: number[] = [];
  values.forEach((v, i) => {
    out.push(i === 0 ? v : alpha * v + (1 - alpha) * out[i - 1]!);
  });
  return out;
}

/** Résultat d'une régression linéaire par moindres carrés. */
export interface LinearRegression {
  slope: number;
  intercept: number;
  r2: number;
  predict: (x: number) => number; // y = slope·x + intercept
}

// Régression linéaire OLS. Requiert >= 2 points avec des x non tous égaux (sinon NaN).
export function linearRegression(points: readonly { x: number; y: number }[]): LinearRegression {
  const n = points.length;
  const nan: LinearRegression = {
    slope: NaN,
    intercept: NaN,
    r2: NaN,
    predict: () => NaN,
  };
  if (n < 2) return nan;

  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const mx = mean(xs);
  const my = mean(ys);

  let sxx = 0;
  let sxy = 0;
  let syy = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i]! - mx;
    const dy = ys[i]! - my;
    sxx += dx * dx;
    sxy += dx * dy;
    syy += dy * dy;
  }
  if (sxx === 0) return nan; // x tous identiques → pente indéfinie

  const slope = sxy / sxx;
  const intercept = my - slope * mx;
  const r2 = syy === 0 ? 1 : (sxy * sxy) / (sxx * syy);
  return { slope, intercept, r2, predict: (x: number) => slope * x + intercept };
}

/** Corrélation de Pearson dans [-1, 1] (NaN si n<2 ou série constante). */
export function pearsonCorrelation(xs: readonly number[], ys: readonly number[]): number {
  const n = Math.min(xs.length, ys.length);
  if (n < 2) return NaN;
  const mx = mean(xs.slice(0, n));
  const my = mean(ys.slice(0, n));
  let sxy = 0;
  let sxx = 0;
  let syy = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i]! - mx;
    const dy = ys[i]! - my;
    sxy += dx * dy;
    sxx += dx * dx;
    syy += dy * dy;
  }
  const denom = Math.sqrt(sxx * syy);
  return denom === 0 ? NaN : sxy / denom;
}

/** Différences successives : out[i] = values[i] - values[i-1]. Longueur = n-1. */
export function deltas(values: readonly number[]): number[] {
  const out: number[] = [];
  for (let i = 1; i < values.length; i++) out.push(values[i]! - values[i - 1]!);
  return out;
}

/** Borne une valeur dans [lo, hi]. */
export function clamp(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}

/** Arrondit à `decimals` décimales (par défaut 0). */
export function round(value: number, decimals = 0): number {
  const f = 10 ** decimals;
  return Math.round(value * f) / f;
}
